"use client"

import { GroupsEnum } from "@/enum/groups"
import { useDialogModal } from "@/hook/handle-modal/hooks/actions"
import { useUploadFileMutation } from "@/services/file"
import { getUserFromLocalStorage } from "@/utils/getUserFromLocalStorage"
import { isUserAdminInGroup } from "@/utils/user"
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenu as UI_DropdownMenu,
} from "components/ui/dropdown-menu"
import { toast } from "components/ui/use-toast"
import { DeleteIcon, Download, PlusCircle, RefreshCcw } from "lucide-react"
import type React from "react"
import { useRef } from "react"
import { useForm } from "react-hook-form"
import { FaSpinner } from "react-icons/fa6"
import type { IDivergence } from "src/@types/divergence"
import { useDivergenceFilterActions } from "../../ducks/filter/hooks/actions"
import { useCurrentConciliation } from "../../ducks/filter/hooks/statusConciliation"
import { AddComment } from "../add-comment"
import { DeleteDivergenceModal } from "../delete-divergence-modal"
import DownloadEvidence from "../download-evidence"

interface FormData {
  evidence: FileList
}

const itemsMenu = [
  {
    label: "Atualizar linha",
    icon: <RefreshCcw size={16} />,
    action: "refresh",
  },
  {
    label: "Adicionar nova evidência",
    icon: <PlusCircle size={16} />,
    action: "add-evidence",
  },
  {
    label: "Download das evidências",
    icon: <Download size={16} />,
    action: "download-evidence",
  },
]

export default function ActionCellTable({
  trigger,
  row,
}: {
  trigger: React.ReactNode
  row: IDivergence
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { register, handleSubmit, reset, setValue } = useForm<FormData>()
  const [uploadFile, { isLoading }] = useUploadFileMutation()
  const { handleModal } = useDialogModal()
  const { isClosed } = useCurrentConciliation()
  const { updateFilter } = useDivergenceFilterActions()

  const onSubmit = async (data: FormData) => {
    try {
      if (data.evidence && data?.evidence?.length > 0) {
        const file = data.evidence[0]

        await uploadFile({
          file,
          entity_name: "divergence",
          entity_id: +row.id,
        }).unwrap()

        toast({
          variant: "default",
          description: "Evidência enviada com sucesso",
          duration: 5000,
        })

        reset()
        if (fileInputRef.current) {
          fileInputRef.current.value = ""
        }
      }
    } catch (error) {
      console.error("Failed to upload file:", error)
      toast({
        variant: "destructive",
        description: "Erro ao realizar ação",
        duration: 5000,
      })
    }
  }

  const handleItemClick = (action: string) => {
    const actionHandlers: { [key: string]: () => void } = {
      refresh: () => {
        updateFilter({
          key: "meta",
          value: { page: 1 },
        })

        toast({
          variant: "default",
          description: "Linha atualizada com sucesso",
          duration: 2000,
        })
      },
      "add-evidence": () => {
        if (!isClosed && fileInputRef.current) {
          fileInputRef.current.click()
        }
      },
      "download-evidence": () => {
        handleModal({
          isOpen: true,
          element: <DownloadEvidence id={row.id as number} isClosed={isClosed} />,
          title: "Download das evidências",
        })
      },
      "add-comment": () => {
        if (!isClosed) {
          handleModal({
            isOpen: true,
            element: <AddComment id={row.id as number} version={row.version} />,
            title: "Adicionar comentário",
          })
        }
      },
      "delete-divergence": () => {
        handleModal({
          isOpen: true,
          element: <DeleteDivergenceModal id={row.id as number} version={row.version} name={row.name} />,
          title: "Excluir Divergência",
        })
      },
    }

    actionHandlers[action]?.()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isClosed && e.target.files && e.target?.files?.length > 0) {
      setValue("evidence", e.target.files)
      handleSubmit(onSubmit)()
    }
  }

  const user = getUserFromLocalStorage()
  const isAdmin = isUserAdminInGroup(user, GroupsEnum.CONCILIACAO)

  return (
    <>
      {isLoading && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center animate-in fade-in duration-200">
          <div className="bg-white rounded-lg shadow-2xl p-8 flex flex-col gap-4 items-center border border-gray-200 animate-in zoom-in-95 duration-200">
            <div className="relative">
              <div className="absolute inset-0 bg-black/5 rounded-full blur-xl" />
              <FaSpinner size={48} className="animate-spin text-black relative" />
            </div>
            <p className="text-sm font-medium text-black">Enviando evidência...</p>
          </div>
        </div>
      )}

      <UI_DropdownMenu>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 p-1">
          {itemsMenu
            .filter((item) => {
              if (isClosed && (item.action === "add-evidence" || item.action === "add-comment")) {
                return false
              }
              return true
            })
            .map((item, index) => (
              <DropdownMenuItem
                key={index}
                onClick={() => handleItemClick(item.action)}
                className="cursor-pointer rounded-md px-3 py-2.5 transition-colors hover:bg-gray-100 focus:bg-gray-100"
              >
                <div className="flex items-center gap-3 w-full">
                  <span className="text-black/70">{item.icon}</span>
                  <span className="text-sm font-medium text-black">{item.label}</span>
                </div>
              </DropdownMenuItem>
            ))}

          {isAdmin && row.is_back_office_origin === false && !isClosed && (
            <>
              <DropdownMenuSeparator className="my-1" />
              <DropdownMenuItem 
                className="cursor-pointer rounded-md px-3 py-2.5 transition-colors hover:bg-red-50 focus:bg-red-50"
                onClick={() => handleItemClick("delete-divergence")}
              >
                <div className="flex items-center gap-3 w-full">
                  <span className="text-red-600">
                    <DeleteIcon size={16} />
                  </span>
                  <span className="text-sm font-medium text-red-600">Apagar Divergência</span>
                </div>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </UI_DropdownMenu>

      <form onSubmit={handleSubmit(onSubmit)} className="hidden">
        <input
          type="file"
          {...register("evidence")}
          onChange={handleFileChange}
          ref={fileInputRef}
          className="hidden"
        />
      </form>
    </>
  )
}
